
import { Progress } from "@/components/ui/progress";

interface DimensionScoresProps {
  dimensionScores: Record<string, number>;
}

export const DimensionScores = ({ dimensionScores }: DimensionScoresProps) => {
  const getScoreDescription = (dimension: string, score: number) => {
    const descriptions: Record<string, string[]> = {
      strategy: [
        "Limited strategic direction for AI initiatives",
        "Emerging AI strategy with basic alignment",
        "Well-defined AI strategy with clear objectives",
        "Comprehensive AI strategy integrated with business",
        "Transformative AI strategy driving innovation"
      ],
      talent: [
        "Basic technical capabilities present",
        "Growing AI skills with gaps",
        "Strong AI talent pool developing",
        "Advanced AI capabilities across teams",
        "World-class AI expertise and leadership"
      ],
      data: [
        "Fragmented data management",
        "Basic data governance in place",
        "Structured data management approach",
        "Advanced data operations and quality",
        "Sophisticated data ecosystem"
      ],
      infrastructure: [
        "Basic technical foundation",
        "Growing technical capabilities",
        "Robust technical infrastructure",
        "Advanced AI/ML platforms",
        "State-of-the-art AI infrastructure"
      ],
      operations: [
        "Ad-hoc AI operations",
        "Basic AI processes established",
        "Structured AI operations",
        "Optimized AI workflows",
        "Automated and efficient AI operations"
      ],
      innovation: [
        "Limited AI innovation focus",
        "Emerging innovation practices",
        "Active AI innovation program",
        "Strong innovation culture",
        "Leading-edge AI innovation"
      ]
    };

    const index = Math.min(Math.floor(score) - 1, 4);
    return descriptions[dimension]?.[Math.max(0, index)] || "Assessment in progress";
  };

  const getProgressColor = (score: number) => {
    if (score <= 1) return "bg-red-500";
    if (score <= 2) return "bg-orange-500";
    if (score <= 3) return "bg-yellow-500";
    if (score <= 4) return "bg-lime-500";
    return "bg-green-500";
  };

  const getScoreColor = (score: number) => {
    if (score <= 1) return "text-red-500";
    if (score <= 2) return "text-orange-500";
    if (score <= 3) return "text-yellow-500";
    if (score <= 4) return "text-lime-500";
    return "text-green-500";
  };

  return (
    <div className="grid gap-6">
      {Object.entries(dimensionScores).map(([dimension, score]) => (
        <div key={dimension} className="space-y-3">
          <div className="flex justify-between items-baseline">
            <div>
              <span className="font-medium capitalize text-base">{dimension}</span>
              <div className="text-sm text-muted-foreground mt-1">
                {getScoreDescription(dimension, score)}
              </div>
            </div>
            <span className={`font-semibold text-lg ${getScoreColor(score)}`}>
              {score.toFixed(1)}/5
            </span>
          </div>
          <Progress
            value={score * 20}
            className={`h-2.5 ${getProgressColor(score)}`}
          />
        </div>
      ))}
    </div>
  );
};
